import React, { useEffect, useState } from "react";
import { TextField, InlineStack, Box, Text } from "@shopify/polaris";

function normalizeHex(raw) {
  const s = String(raw || "").trim();
  if (!s) return "#000000";
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s;
  if (/^[0-9a-fA-F]{6}$/.test(s)) return `#${s}`;
  return s;
}

function FieldLabel({ children }) {
  return (
    <div className="fsb-design-field__label">
      <Text as="span" variant="bodyMd" fontWeight="medium">
        {children}
      </Text>
    </div>
  );
}

/** Hex color field with a native swatch picker — label above, aligned with PxField. */
export function ColorField({ label, value, onChange }) {
  const val = normalizeHex(value);
  const swatch = /^#[0-9a-fA-F]{6}$/.test(val) ? val : "#000000";

  return (
    <div className="fsb-design-field">
      <FieldLabel>{label}</FieldLabel>
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <input
          type="color"
          value={swatch}
          onChange={(e) => onChange(e.target.value)}
          className="fsb-design-field__swatch"
          aria-label={label}
        />
        <Box minWidth={0} width="100%">
          <TextField
            labelHidden
            value={val}
            onChange={(v) => onChange(normalizeHex(v))}
            autoComplete="off"
          />
        </Box>
      </InlineStack>
    </div>
  );
}

/** Numeric field with a px suffix — keeps local text while typing. */
export function PxField({ label, value, onChange, min = 0, max = 200 }) {
  const [text, setText] = useState(String(value ?? 0));

  useEffect(() => {
    setText(String(value ?? 0));
  }, [value]);

  const commit = (raw) => {
    const trimmed = String(raw).trim();
    if (trimmed === "" || trimmed === "-") {
      setText(String(min));
      onChange(min);
      return;
    }
    let n = Number(trimmed);
    if (!isFinite(n)) {
      setText(String(value ?? min));
      return;
    }
    n = Math.max(min, Math.min(max, n));
    setText(String(n));
    onChange(n);
  };

  return (
    <div className="fsb-design-field">
      <TextField
        label={label}
        type="number"
        value={text}
        onChange={(v) => {
          setText(v);
          const trimmed = String(v).trim();
          if (trimmed === "" || trimmed === "-") return;
          const n = Number(trimmed);
          if (!isFinite(n)) return;
          onChange(Math.max(min, Math.min(max, n)));
        }}
        onBlur={() => commit(text)}
        suffix="px"
        autoComplete="off"
        min={min}
        max={max}
      />
    </div>
  );
}
